import { z } from 'zod';

// src/connect/types.ts
var ContactSourcePlatform = {
  Dashboard: "dashboard",
  Website: "website"
};
var StartChatInputSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  email: z.string().trim().min(1, "Email is required").max(254, "Email is too long").email("Enter a valid email address"),
  message: z.string().trim().max(4e3, "Message is too long").optional(),
  subject: z.string().trim().max(200, "Subject is too long").optional(),
  /** Free-form short slug (`dashboard` | `website` | …). Defaults server-side. */
  source_platform: z.string().trim().max(40).optional()
});
var StartChatHandshakeSchema = z.object({
  contact_id: z.string().min(1),
  participant_id: z.string().min(1),
  participant_token: z.string().min(1),
  region: z.string().min(1)
});
function parseStartChatInput(input) {
  return StartChatInputSchema.parse(input);
}
function parseStartChatHandshake(data) {
  return StartChatHandshakeSchema.parse(data);
}

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  PERM002: spec("PERM002", "Resource access denied", "You do not have access to this resource within your tenant.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/connect/client.ts
var CONNECT_CHAT_PATH = "/connect/chat";
async function startChat(api, input, opts = {}) {
  const payload = parseStartChatInput(input);
  const res = await api.post(CONNECT_CHAT_PATH, payload, opts.request);
  return parseStartChatHandshake(unwrapData(res));
}
var ConnectChatClient = class {
  constructor(api) {
    this.api = api;
  }
  api;
  startChat(input, opts) {
    return startChat(this.api, input, opts);
  }
};

// src/connect/session.ts
async function loadChatJs() {
  const moduleSpecifier = "amazon-connect-chatjs";
  let mod;
  try {
    mod = await import(
      /* @vite-ignore */
      moduleSpecifier
    );
  } catch (error) {
    throw new Error(
      "amazon-connect-chatjs is not installed. Add it to the host app to use createChatSession(): " + (error instanceof Error ? error.message : String(error))
    );
  }
  const candidate = mod;
  const fromModule = candidate.ChatSession ? candidate : candidate.default;
  if (fromModule && fromModule.ChatSession) return fromModule;
  const glob = globalThis;
  if (glob.connect?.ChatSession) return glob.connect;
  throw new Error("amazon-connect-chatjs loaded but no ChatSession factory was found.");
}
async function createChatSession(handshake, opts = {}) {
  const connect = await loadChatJs();
  const region = opts.region ?? handshake.region;
  connect.ChatSession.setGlobalConfig({
    region,
    loggerConfig: { level: opts.logLevel ?? "ERROR" }
  });
  return connect.ChatSession.create({
    chatDetails: {
      contactId: handshake.contact_id,
      participantId: handshake.participant_id,
      participantToken: handshake.participant_token
    },
    type: connect.ChatSession.SessionTypes.CUSTOMER,
    options: { region }
  });
}

export { CONNECT_CHAT_PATH, ConnectChatClient, ContactSourcePlatform, StartChatHandshakeSchema, StartChatInputSchema, createChatSession, parseStartChatHandshake, parseStartChatInput, startChat };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map